import { inject, Injectable, signal } from "@angular/core";
import {
  AnnualStatementPageFilter,
  AnnualStatementStatus,
  SigningStatus
} from "@models/annual-statements/annual-statement-enums";
import { AnnualStatementRecord } from "@models/annual-statements/annual-statement-record";
import { Tag } from "@models/common/tag";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterOption } from "@models/grid/grid-filter";
import { PageResult } from "@models/requests/page-result.interface";
import {
  AnnualStatementsGridColumns
} from "@pages/annual-statements/components/annual-statements-page/annual-statements-page-configs";
import {
  AgAnnualNameAcnComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-name-acn/ag-annual-name-acn.component";
import {
  AgAnnualSigningStatusComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-signing-status/ag-annual-signing-status.component";
import {
  AgAnnualStatusComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-status/ag-annual-status.component";
import {
  AgCompanyDebtComponent
} from "@shared/components/grid/ag-cells/common/ag-company-debt/ag-company-debt.component";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import { AgDebtStatusComponent } from "@shared/components/grid/ag-cells/common/ag-debt-status/ag-debt-status.component";
import { AgEntityTagsComponent } from "@shared/components/grid/ag-cells/common/ag-entity-tags/ag-entity-tags.component";
import {
  AgReminderTagComponent
} from "@shared/components/grid/ag-cells/common/ag-remider-tag/ag-reminder-tag.component";
import { AnnualStatementsApiService } from "@shared/services/annual-statements-api.service";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { dateComparator } from "@shared/utils/grid/date-comparator";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams } from "ag-grid-community";
import { Subject } from "rxjs";

@Injectable()
export class AnnualStatementsGridService extends BaseGridService {
  annualStatementsApiService = inject(AnnualStatementsApiService);

  records = signal<AnnualStatementRecord[]>([]);
  totalRecords = signal<number>(0);
  dataSource = signal<IServerSideDatasource>(this.getDataSource());

  dataLoaded$ = new Subject<PageResult<AnnualStatementRecord>>();

  pageFilter!: AnnualStatementPageFilter;

  // filters
  readonly statusFilterOptions: FilterOption[] = convertEnumToFilterOption(AnnualStatementStatus);
  readonly signingStatusFilterOption: FilterOption[] = convertEnumToFilterOption(SigningStatus);
  readonly debtStatusFilterOptions: FilterOption[] = [
    { label: 'Paid', value: 'true', active: false },
    { label: 'Unpaid', value: 'false', active: false }
  ];

  canBeOpened = false;
  profileUserNameList: string[] = [];
  accountManagerFilterOptions: FilterOption[] = [];
  partnerManagerFilterOptions: FilterOption[] = [];
  tagsList: string[] = [];
  tagsFilterOption: FilterOption[] = [];

  setAnnualStatementPageFilter(pageFilter: AnnualStatementPageFilter): void {
    this.pageFilter = pageFilter;
  }

  setGridFilterOptions(
    canBeOpened: boolean,
    profileUserNameList: string[],
    tagsList: string[]
  ): void {
    this.canBeOpened = canBeOpened;
    this.profileUserNameList = profileUserNameList;
    this.accountManagerFilterOptions = uniqueArrayToFilterOptionHelper(profileUserNameList);
    this.partnerManagerFilterOptions = uniqueArrayToFilterOptionHelper(profileUserNameList);
    this.tagsList = tagsList;
    this.tagsFilterOption = uniqueArrayToFilterOptionHelper(tagsList);
  }

  setDataSource(searchText?: string, endRow?: number): void {
    this.dataSource.set(this.getDataSource(searchText, endRow));
  }

  getDataSource(searchText?: string, startRow?: number, endRow?: number): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const pageFilter = this.pageFilter;
        this.annualStatementsApiService.getAnnualList(params.request, pageFilter, searchText, startRow, endRow)
          .subscribe({

            next: result => {
              this.records.set(result.records);
              this.totalRecords.set(result.total);

              params.success({
                rowData: result.records,
                rowCount: result.total,
              });

              this.dataLoaded$.next(result);
            },
            error: (err) => {
              console.error(err);
              params.fail();
            }
          });
      }
    };
  }

  setColDefsWithConfig(colDefsKeys: AnnualStatementsGridColumns[], actionColumn: ColDef) {
    const configColDefs = colDefsKeys.map((key) => this.getColDefByKey(key));
    const colDefs: ColDef[] = [
      ...configColDefs,
      actionColumn
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }

  getColDefByKey(key: AnnualStatementsGridColumns): ColDef {
    const colDefsRecord: Record<AnnualStatementsGridColumns, ColumnWithExportName> = {
      [AnnualStatementsGridColumns.CompanyNameAcn]: {
        headerName: 'Company Name & ACN',
        field: 'companyName',
        suppressMovable: true,
        width: 200,
        flex: 1,
        editable: false,
        sort: 'asc',
        cellClass: 'h-100 d-flex align-items-center',
        cellRenderer: AgAnnualNameAcnComponent,
        cellRendererParams: { canBeOpened: this.canBeOpened },
        menuTabs: ['generalMenuTab'],
      },
      [AnnualStatementsGridColumns.Acn]: {
        headerName: '',
        field: 'acn',
        width: 100,
        flex: 0.5,
        menuTabs: [],
        hide: true
      },
      [AnnualStatementsGridColumns.Status]: {
        headerName: 'Status',
        field: 'status',
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.statusFilterOptions,
          values: Object.values(AnnualStatementStatus).filter((v) => !isNaN(Number(v))),
        },
        cellRenderer: AgAnnualStatusComponent,
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.DebtStatus]: {
        headerName: 'Debt Status',
        field: 'isPaid',
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.debtStatusFilterOptions,
          values: [true, false]
        },
        cellRenderer: AgDebtStatusComponent,
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.Partner]: {
        headerName: 'Partner',
        field: 'partnerManagerFullName',
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.partnerManagerFilterOptions,
          values: this.profileUserNameList,
        },
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.ReviewDate]: {
        headerName: 'Review Date',
        field: 'reviewDate',
        width: 100,
        filter: 'agDateColumnFilter',
        filterParams: { comparator: dateComparator },
        cellRenderer: AgDateCellComponent,
        cellDataType: CellDataType.DATE,
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.PaymentDeadline]: {
        headerName: 'Payment Deadline',
        field: 'paymentDeadline',
        width: 120,
        filter: 'agDateColumnFilter',
        filterParams: { comparator: dateComparator },
        cellRenderer: AgDateCellComponent,
        cellDataType: CellDataType.DATE,
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.Debt]: {
        headerName: 'Debt',
        field: 'debt',
        flex: 0.5,
        cellRenderer: AgCompanyDebtComponent,
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.Tags]: {
        headerName: 'Tags',
        field: 'tags',
        flex: 1,
        sortable: false,
        cellRendererParams: { allowEdit: false },
        cellRenderer: AgEntityTagsComponent,
        valueFormatter: (params: { value: Tag }) => params.value?.name ?? '',
        menuTabs: [],
        hide: true
      },
      [AnnualStatementsGridColumns.TagsName]: {
        headerName: 'Tags',
        field: 'tagsName',
        suppressColumnsToolPanel: true,
        lockVisible: true,
        sortable: false,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.tagsFilterOption,
          values: this.tagsList,
        },
        cellRendererParams: { allowEdit: false },
        menuTabs: [],
      },
      [AnnualStatementsGridColumns.SigningStatus]: {
        headerName: 'Signing Status',
        field: 'signingStatus',
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.signingStatusFilterOption,
          values: Object.values(SigningStatus).filter((v) => !isNaN(Number(v))),
        },
        cellRenderer: AgAnnualSigningStatusComponent,
        menuTabs: [],
        hide: true
      },
      [AnnualStatementsGridColumns.SigningDate]:
        {
          headerName: 'Signing Date',
          field: 'signingDate',
          flex: 0.5,
          filterParams: { comparator: dateComparator },
          cellRenderer: AgDateCellComponent,
          menuTabs: [],
          hide: true
        },
      [AnnualStatementsGridColumns.AccountManager]: {
        headerName: 'Account Manager',
        field: 'accountManagerFullName',
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.accountManagerFilterOptions,
          values: this.profileUserNameList,
        },
        menuTabs: [],
        hide: true
      },
      [AnnualStatementsGridColumns.PaymentReminders]: {
        headerName: 'Payment Reminders',
        field: 'paymentReminders',
        flex: 0.5,
        menuTabs: [],
        sortable: false,
        cellRenderer: AgReminderTagComponent,
        hide: true
      },
      [AnnualStatementsGridColumns.SigningReminders]: {
        headerName: 'Signing Reminders',
        field: 'signingReminders',
        flex: 0.5,
        menuTabs: [],
        sortable: false,
        cellRenderer: AgReminderTagComponent,
        hide: true
      },
    };

    return colDefsRecord[key];
  }
}
